// Mağaza katalog uç noktaları. Paketler login gerektirmeden görüntülenebilir
// (mobildeki gibi), bu yüzden bu istekler herkese açıktır.

import api from '../../api/client';
import { unwrap, toApiError } from '../../api/unwrap';

// GET /countries-data → [{ country, countrySelected, flag, isActive, products[] }]
export const getCountries = async () => {
  try {
    return (await api.get('/countries-data').then(unwrap)) || [];
  } catch (error) {
    throw toApiError(error);
  }
};

// GET /regions-data-light → [{ id, region, flag, isActive, originalDataId }]
export const getRegions = async () => {
  try {
    return (await api.get('/regions-data-light').then(unwrap)) || [];
  } catch (error) {
    throw toApiError(error);
  }
};

// GET /homepage → { banners, popularCountries, popularPackages, popularRegions, continents }
export const getHomepage = async () => {
  try {
    return await api.get('/homepage').then(unwrap);
  } catch (error) {
    throw toApiError(error);
  }
};

// Bölgesel sekme için kıtalar (kullanıcı dostu adlar: Global, Avrupa, Asya…).
export const getContinents = async () => {
  try {
    return (await api.get('/continents').then(unwrap)) || [];
  } catch (error) {
    throw toApiError(error);
  }
};

// Tek ülke detayı: GET /countries-data/{countryName} → { products[].refills[] }
const getCountryDetail = async (countryName) => {
  const list = await api
    .get(`/countries-data/${encodeURIComponent(countryName)}`)
    .then(unwrap);
  const c = Array.isArray(list) ? list[0] : list;
  if (!c) return null;
  return { name: c.countrySelected || c.country, flag: c.flag, products: c.products || [] };
};

// Bölgesel (kıtalararası) ve ömürlük eSIM'ler backend'de iki AYRI katalogdur;
// admin panelde ayrı ayrı açılıp kapatılır ve mobil uygulama da bunları asla
// birleştirmez (bkz. Utkan_Mobile → AllCountriesRepository.getSpecificCountry).
// Bu yüzden burada da tek bir uç nokta seçilir, ikisi birden çağrılmaz.
export const REGION_ENDPOINTS = {
  region: 'regions-data',
  lifetime: 'lifetime-data',
};

// Kıta detayı: /regions-data/{id} veya /lifetime-data/{id}
// Yanıt tek entry döndürür ama ürün listesi çoklu olabilir.
//
// DİKKAT: entry.flag bölgeye ait bir görsel DEĞİL — backend bunu entry'deki ilk
// ürünün `img` alanından kopyalıyor (ör. Kuzey Amerika → Volans görseli). Bunu
// sayfa başlığında kullanmak, üç ayrı ürünün hepsini tek ürünün görseliyle
// temsil etmek demek. Bu yüzden ürün görselleriyle çakışan flag'i düşürüyoruz;
// başlıkta globe ikonu çıkar, her ürün kendi `img`'ini gösterir.
const getRegionDetail = async (type, continentId) => {
  const endpoint = REGION_ENDPOINTS[type];
  if (!endpoint) return null;

  // Bölgenin adı ("Kuzey Amerika") detay yanıtında YOK; oradaki `country` alanı
  // ürün adıdır (Volans). Ad /continents listesinden id ile çözülür — aksi
  // halde sayfa doğrudan açıldığında (router state yokken) başlıkta ham id
  // kalıyordu. Liste alınamazsa başlık router state'e / boşa düşer.
  const [list, continents] = await Promise.all([
    api.get(`/${endpoint}/${continentId}`).then(unwrap),
    getContinents().catch(() => []),
  ]);
  const entries = Array.isArray(list) ? list : [list].filter(Boolean);
  const continent = (continents || []).find(
    (c) => String(c.id) === String(continentId)
  );

  const products = [];
  const productImages = new Set();
  let flag = null;
  const seen = new Set();
  entries.forEach((entry) => {
    if (!entry) return;
    if (!flag && entry.flag) flag = entry.flag;
    (entry.products || []).forEach((p) => {
      if (seen.has(p.originalId)) return;
      seen.add(p.originalId);
      if (p.img) productImages.add(p.img);
      products.push(p);
    });
  });

  return {
    name: continent?.name || null,
    flag: flag && productImages.has(flag) ? null : flag,
    products,
  };
};

// type'a göre detay getirir: country | region | lifetime.
export const getEsimDetail = async (type, id) => {
  try {
    if (type === 'country') return await getCountryDetail(id);
    return await getRegionDetail(type, id);
  } catch (error) {
    throw toApiError(error);
  }
};

// --- Katalog v2 -------------------------------------------------------------
// GET /v2/bundles?family=&scope=&country=&region=
// Yeni ürün aileleri (bugün: sınırsız) için TEK uç. Yanıt:
// [{ id, providerRef, scope, family, formFactor, title, description, imgUrl,
//    countries[], regions[], plans[{ id, title, isUnlimited, dataMb?,
//    validityDays, priceEur, priceTry, priceUsd, priceId, versionedPriceEur,
//    sortOrder }] }]
//
// `Platform: web` başlığını client.js zaten ekliyor, bu yüzden ayrıca
// `platform` query'si göndermiyoruz — backend yalnızca web'e açık planları döner.
// Tanınmayan family/scope değerleri 400 + code:INVALID_FAMILY/INVALID_SCOPE
// döndürür (sessizce yutulmaz); eşleşmeyen country/region ise boş dizidir.
export const getBundles = async (params = {}) => {
  try {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    ).toString();
    return (await api.get(`/v2/bundles${qs ? `?${qs}` : ''}`).then(unwrap)) || [];
  } catch (error) {
    throw toApiError(error);
  }
};

// Tek paket (bundle) detayı: GET /bundle/{bundleId}/{refillId}
// bundleId = product.originalId · refillId = refill.id
// → { id, name, bundle_type, description, coverage[], networks[], refill{} }
export const getPlanDetail = async (bundleId, refillId) => {
  try {
    return await api.get(`/bundle/${bundleId}/${refillId}`).then(unwrap);
  } catch (error) {
    throw toApiError(error);
  }
};
